import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, connect } from 'react-redux'
import qs from 'qs'
import Loader from '~components/Loader'
import Heading from '~components/Heading'
import { loadDir } from '~redux/mediaHub'
import { pageView } from '~helpers/ga'

const MediaHub = ({ dirs }) => {
  const [dir, setDir] = useState('/')
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    pageView()
    const query = qs.parse(location.search, { ignoreQueryPrefix: true })
    const directory = query.dir || '/'
    handleDirectoryClick(directory)
  }, [location.search, dispatch])

  const basename = (path) => {
    const parts = path.split('/')
    return parts[parts.length - 1]
  }

  const handleDirectoryClick = (directory) => {
    if (directory !== '/' && (dir === directory)) return
    const isRoot = directory === '/'
    const query = isRoot ? '' : `?dir=${directory}`
    setDir(directory)
    navigate(`/media-hub${query}`)
    dispatch(loadDir(directory))
  }

  const breadcrumbs = () => {
    let pathParts = [
      {
        title: 'Media Hub',
        dir: '/'
      }
    ]

    if (dir !== '/') {
      const parts = dir.split('/')
      const constructedPath = []
      pathParts = [
        ...pathParts,
        ...parts.map(part => {
          constructedPath.push(part)
          return {
            title: part,
            dir: constructedPath.join('/')
          }
        })
      ]
    }

    return pathParts
  }

  const current = dirs[dir]

  if (!current) {
    return <Loader />
  }

  const breadcrumbItems = breadcrumbs()

  return (
    <section className="w-full my-10 xl:my-20">
      <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
        <div className="inline-flex mb-3 md:mb-2 ">
          {breadcrumbItems.map((crumb, index) => (
            <span key={index}>
              {index === breadcrumbItems.length - 1 && (
                <span className="mx-1">
                  {crumb.title}
                </span>
              )}

              {index !== breadcrumbItems.length - 1 && (
                <>
                  <a
                    href="#"
                    className="mx-1 hover:underline focus:underline"
                    onClick={e => { e.preventDefault(); handleDirectoryClick(crumb.dir) }}
                  >
                    {crumb.title}
                  </a>

                  <span className="inline">
                    /
                  </span>
                </>
              )}
            </span>
          ))}
        </div>
      </div>

      <Heading
        title={dir === '/' ? 'Media Hub' : basename(dir)}
        className="mb-6"
      />

      <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
        <div className="flex flex-wrap -mx-1 -my-1 md:-mx-2 md:-my-3 xxl:-mx-5 xl:-my-10">
          {current.directories.map(dir => (
            <button type="button" onClick={() => handleDirectoryClick(dir.path)} className="w-1/2 px-1 my-1 md:w-1/3 lg:w-1/5 md:px-2 xxl:px-5 md:my-3 xl:my-10" key={`${dir}_${dir.path}`}>
              <div className="relative block w-full h-0 overflow-hidden text-sm text-center text-white bg-charcoal md:text-md lg:text-sm xl:text-md group pt-9/12">
                <span className="absolute left-0 right-0 z-10 block transition duration-150 transform -translate-y-1/2 opacity-100 top-1/2 group-hover:scale-300 group-hover:opacity-0 group-focus:scale-300 group-focus:opacity-0">
                  {dir.name}
                </span>

                {dir.thumbnail &&
                  <img src={dir.thumbnail} alt={dir.name} className="absolute top-0 left-0 right-0 block object-cover w-full h-full transition-opacity duration-150 ease-in-out opacity-60 group-hover:opacity-100 group-focus:opacity-100" />}
              </div>
            </button>
          ))}

          {current.files.map(file => (
            <a href={file.url} target="_blank" rel="noreferrer" className="w-1/2 px-1 my-1 md:w-1/3 lg:w-1/5 md:px-2 xxl:px-5 md:my-3 xl:my-10" key={`${dir}_${file.path}`}>
              <div className="relative block w-full h-0 overflow-hidden text-sm text-center text-white bg-charcoal md:text-md lg:text-sm xl:text-md group pt-9/12">
                <span className="absolute left-0 right-0 z-10 block transition duration-150 transform -translate-y-1/2 opacity-100 top-1/2 group-hover:scale-300 group-hover:opacity-0 group-focus:scale-300 group-focus:opacity-0">
                  {basename(file.name)}
                </span>

                {file.thumbnail &&
                  <img src={file.thumbnail} alt={file.name} className="absolute top-0 left-0 right-0 block object-cover w-full h-full transition-opacity duration-150 ease-in-out opacity-60 group-hover:opacity-100 group-focus:opacity-100" />}
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}

MediaHub.propTypes = {
  dirs: PropTypes.object
}

const mapStateToProps = (state) => ({
  dirs: state.mediaHub.dirs
})

export default connect(mapStateToProps)(MediaHub)
